<template lang="pug">
main.container.my-8.lg_mt-0
  ClientOnly
    .discover(:class="breakpoint")
      .flex.md_py-12
        //- head
        h2.hidden.m-0.text-5xl.font-sans.font-semibold.md_block Discover
        .flex-1
          .flex.md_justify-end.items-center.gap-4
            .relative.flex.flex-1.md_flex-initial
              form.flex-1(@submit.prevent="searchSubmit")
                input.border-solid.outline-none.border.border-black.rounded-full.px-5.w-full.md_flex-none.md_search-input(
                  placeholder="Search"
                  @input="debounceListener"
                  :value="searchValue"
                  ref="searchInput"
                  type="text"
                  inputmode="search"
                  class="py-2.5"
                )
              .absolute.inset-y-0.right-0
                SearchIcon.h-full.cursor-pointer.transform.scale-75.mr-1(v-if="searchValue.length === 0")
                XIcon.h-full.w-10.cursor-pointer.transform.scale-50.mr-1(v-else @click.prevent="clearSearch")
            FilterIcon.md_hidden.flex-shrink-0.cursor-pointer(width="19" height="15" @click.prevent="toggleFilterModal")
            SortIcon.md_hidden.flex-shrink-0.cursor-pointer(width="19" height="15" @click.prevent="toggleSortModal")

      .flex
        VFullscreenMenu(:open="filterModalOpen")
          .flex.flex-col.h-full
            .flex-1.overflow-y-scroll.md_overflow-visible.p-6.md_p-0.md_pr-5
              Filters.m-w-0.md_mt-3.md_w-56(
                :options="filterOptions"
                :userSelectedOptions="userSelectedOptions"
                @set-select-option="setSelectOption($event)"
                @set-range="setRange($event)"
                @reset="resetAll"
              )
            .block.w-full.p-6.text-center.md_hidden
              .mb-3.text-gray-850 {{ textCount }}
              VButton(@click="toggleFilterModal" variant="dark") Show Results
        .flex-1.py-6.md_pl-6.md_py-0
          //- right pane
          VTabs.text-xl.lg_text-xl.mb-4(:items="tabs" @change="gotoTab")
          VFullscreenMenu(:open="sortModalOpen")
            .block.h-full.md_hidden
              //- use buttons when mobile layout, pulldown list for everything else.
              ul.h-full.flex.flex-1.flex-col.justify-center
                li
                  .text-xl.font-sans.font-semibold.text-center.mb-6 Sort by
                li.text-center.my-1(v-for="option in sortOptions")
                  VButton.border.border-black(
                    :variant="`${isSortOptionSelected(option) ? 'dark' : 'light'}`"
                    @click="selectSort(option.endpoint_value)"
                  ) {{ option.label }}
            .hidden.md_block
              .sort_container.md_pt-3.md_pb-5.md_text-right
                label.font-sans.font-semibold.mr-2(for="sort_by") Sort By:
                select.sort_select.appearance-none.bg-transparent.outline-none.pr-6.border-0.focus__shadow-none(
                  @change="e => setSort(e.target.value)"
                  name="sort_by"
                )
                  option(v-for="option in sortOptions" :value="option.endpoint_value" :selected="option.selected") {{ option.label }}
          //- records
          VGrid
            li.mb-2(v-for="(record, index) in data" :key="`${endpoint}-${index}`")
              VrifiedCompanyCard(
                v-if="endpoint === Endpoints.COMPANIES"
                :company="record"
                :isComingSoon="comingSoon(record)"
                :followingCompanies="followingCompanies"
                @on-follow="handleFollowCompany($event)"
              )
              .relative(v-if="endpoint === Endpoints.PROJECTS" @click.prevent.stop="goToProject(record)")
                VTile(:imgSrc="record.thumbnail" :title="record.title")
                  template(v-if="record.companies && record.companies.length" v-slot:subtitle) {{ record.companies[0].name }}
                  VPermissionsBadge(v-if="record.view_access_type" :accessType="record.view_access_type" v-slot:badge)
                    span {{ record.view_access_type }}
              .relative(v-if="endpoint === Endpoints.DECKS")
                a(@click.prevent.stop="goToDeck(record)")
                  VTile(:imgSrc="record.thumbnail" :title="record.title")
                    template(v-if="record.company" v-slot:subtitle) {{ record.company.name }}
                    template(v-if="record.view_access_type" v-slot:badge)
                      VPermissionsBadge(v-if="record.view_access_type" accessType="record.view_access_type" v-slot:badge)
                        span {{ record.view_access_type }}
          div(ref="LoadMoreRef")
          LoadingSpinner.mb-20.mt-12(v-if="loading")
          .mt-12.text-center(v-else)
            .my-3.text-gray-850
              span {{ textCount }}
            VButton(v-if="count > data.length" @click="append") See More
</template>

<script lang="ts" setup>
import XIcon from '@/components/icons/x.svg'
import SortIcon from '@/components/icons/Sort.svg'
import FilterIcon from '@/components/icons/Filter.svg'
import SearchIcon from '@/components/icons/Search.svg'
import type { Company } from '@/entities/company'
import type { SortOption } from '@/entities/filter'
import debounce from '@/lib/debounce'
import type { QueryOptions } from '@/composables/useSearch'

interface Options {
  limit: number
  is_vrified: boolean
  view_access_type: 'public' | 'private'
  query?: string
}

const defaultOptions: Options = Object.freeze({
  limit: 12,
  is_vrified: true,
  view_access_type: 'public',
})

enum Endpoints {
  COMPANIES = 'companies',
  DECKS = 'decks',
}

const endpointKeys = Object.keys(Endpoints) as (keyof typeof Endpoints)[]

const label = Object.freeze({
  [Endpoints.COMPANIES]: 'Company',
  [Endpoints.DECKS]: '3D Presentation',
} as const)

// plural
const labels = Object.freeze({
  [Endpoints.COMPANIES]: 'Companies',
  [Endpoints.DECKS]: '3D Presentations',
} as const)

interface TabOption {
  label: string
  value: Endpoints
  selected?: boolean
}

const defaultEndpoint = Endpoints.COMPANIES

const route = useRoute()
const router = useRouter()
const { $analytics } = useNuxtApp()

const sortModalOpen = ref(false)
const filterModalOpen = ref(false)
const options = ref(defaultOptions)
const alternateEndpointData = ref<{ type: string; count: number; results: any[] }[]>([])
const searchInput = ref<HTMLInputElement | null>(null)
const tabs = ref<TabOption[]>([
  { label: labels[Endpoints.COMPANIES], value: Endpoints.COMPANIES },
  { label: labels[Endpoints.DECKS], value: Endpoints.DECKS },
])

const endpoint = ref(
  (() => {
    const endpointQs = route.params.endpoint
    const endpoint = endpointKeys.find(key => Endpoints[key] === endpointQs)

    return endpoint ? Endpoints[endpoint] : defaultEndpoint
  })()
)

const { followCompany } = useFollowCompany()

let breakpoint, isMobileBreakpoint
if (process.client) {
  const mediaQuery = useMediaQuery()
  breakpoint = mediaQuery.breakpoint
  isMobileBreakpoint = mediaQuery.isMobileBreakpoint
}

const { state: userState } = useUser()
const { goToDeck, goToProject } = useEndpointNavigation()
const { enableBodyScroll, disableBodyScroll } = useBodyScrollLock()
const { debouncedValue: search, displayValue: searchValue, debounceListener } = useDebounce(500)
const { count, loading, query, data, append } = useSearch()
const {
  filters,
  sortOptions,
  currentSort,
  filterOptions,
  userSelectedOptions,
  setSort,
  setRange,
  setSelectOption,
  resetFiltersAndSortOptions,
} = useFilters()
const { LoadMoreRef } = useInfiniteScroll(entries => {
  if (entries[0].intersectionRatio <= 0 || !data.value.length || count.value === data.value.length) return
  append()
})

const followingCompanies = computed(() => userState.value.followingCompanies)

const textCount = computed(() => {
  if (count.value === 0) {
    const hasSearched = searchValue.value?.trim()
    return `0 ${labels[endpoint.value]} ${hasSearched ? `matched your search for "${searchValue.value}"` : 'found'}`
  }
  if (count.value === 1) {
    return `1 ${label[endpoint.value]} found`
  }
  if ((count.value > 1 && count.value <= data.value.length) || filterModalOpen.value) {
    return `${count.value} ${labels[endpoint.value]} found`
  }
  // for paginated views
  return `${data.value.length} of ${count.value} ${labels[endpoint.value]}`
})

onMounted(async () => {
  $analytics.track('showedScreenDiscover', {
    endpoint: endpoint.value,
  })

  if (route.query.followCompanyId) {
    await followCompany({
      id: route.query.followCompanyId as string,
      name: route.query.followCompanyName as string,
    })

    router.replace({ query: {} })
  }
})

if (breakpoint && isMobileBreakpoint) {
  watch(breakpoint, () => {
    if (!isMobileBreakpoint!()) {
      enableBodyScroll()
      sortModalOpen.value = false
      filterModalOpen.value = false
    }
  })
}

watch([filters, search] as const, ([filters, search]) => {
  $analytics.track(`discover${labels[endpoint.value]}Searched`, {
    ...filters,
    search,
  })

  options.value = {
    ...resetOptions(),
    ...filters,
    query: search,
  }
})

const debouncedWatchQuery = debounce(([endpoint, options]: [Endpoints, Partial<QueryOptions>]) => {
  const opts = {
    ...options,
    // include company details into data results
    ...(endpoint === Endpoints.COMPANIES && { 'include[]': 'stock' }),
    ...(endpoint === Endpoints.DECKS && { 'include[]': 'company' }),
  }

  query({
    type: endpoint,
    options: opts,
  })

  // Query results for endpoints other than current selected
  const alternateEndpoints = endpointKeys.filter(key => Endpoints[key] !== endpoint)
  const alternateEndpointResults = alternateEndpoints.map(key =>
    query({
      type: Endpoints[key],
      options: opts,
      updateState: false,
    })
  )

  Promise.all(alternateEndpointResults).then(results => (alternateEndpointData.value = results))
}, 500)

watch([endpoint, options] as const, debouncedWatchQuery)

watch([count, alternateEndpointData, loading] as const, ([count, alternateEndpointData, loading]) => {
  if (loading) return

  tabs.value = tabs.value.map(tab => {
    const endpointData = alternateEndpointData.filter(item => item.type === tab.value)
    const endpointCount = endpointData.length ? endpointData[0].count : 0 || count
    const tabCount = endpointCount > 0 ? ` (${endpointCount})` : ''

    tab.selected = endpoint.value === tab.value
    tab.label = labels[tab.value] + tabCount
    return tab
  })
})

watch([sortModalOpen, filterModalOpen] as const, ([sortModalOpen, filterModalOpen]) => {
  if (sortModalOpen || filterModalOpen) {
    disableBodyScroll()
  } else {
    enableBodyScroll()
  }
})

function searchSubmit() {
  searchInput.value?.blur()
}

function clearSearch() {
  search.value = ''
  searchValue.value = ''
}

function toggleFilterModal() {
  filterModalOpen.value = !filterModalOpen.value
}

function toggleSortModal() {
  sortModalOpen.value = !sortModalOpen.value
}

function gotoTab(value: Endpoints) {
  endpoint.value = value
  history.pushState({}, '', '/discover/' + value)
  $analytics.track(`discover${labels[value]}TabSelected`)
}

function comingSoon(company: Company) {
  return company.is_coming_soon
}

function isSortOptionSelected(option: SortOption) {
  return currentSort.value.endpoint_value === option.endpoint_value
}

function selectSort(value: string) {
  setSort(value)
  sortModalOpen.value = false
}

function resetOptions(): Options {
  let clearedOptions = {}
  Object.keys(defaultOptions).map(key => {
    // @ts-ignore
    clearedOptions[key] = options.value[key]
  })
  return clearedOptions as any
}

function resetAll() {
  $analytics.track(`discover${labels[endpoint.value]}Reset`)
  resetFiltersAndSortOptions()
  clearSearch()
}

async function handleFollowCompany(selected: { id: string; name: string }) {
  if (!userState.value.user) {
    window.location.href = `/login?redirectedFrom=${window.location.pathname}&followCompanyId=${selected.id}&followcompanyName=${selected.name}`
  } else {
    await followCompany(selected)
  }
}
</script>

<style lang="stylus" scoped>
.sort_select
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23979797' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-down'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")
  background-repeat: no-repeat, repeat
  background-position: right 0 top 50%, 0 0
</style>
